// IOSLifecycleManager.js
import { App } from '@capacitor/app';
import { tokenManager } from './TokenManager';
import { secureTokenStorage } from './SecureTokenStorage';
import { Preferences } from '@capacitor/preferences';
import { isCapacitor } from '../utils/platform';
import { tokenLog, tokenWarn, tokenError, createLogger } from './Debug';

class IOSLifecycleManager {
  constructor() {
    this.isInitialized = false;
    this.lastActiveTime = Date.now();
    this.backgroundTaskId = null;
  }

  async initialize() {
    if (!isCapacitor || this.isInitialized) {
      return;
    }

    try {
      
      
      // Listen for app state changes
      App.addListener('appStateChange', ({ isActive }) => {
        this.handleAppStateChange(isActive);
      });

      // Listen for app URL opens
      App.addListener('appUrlOpen', (data) => {
        
      });

      // Listen for app restore
      App.addListener('appRestoredResult', (data) => {
        
        this.handleAppRestore();
      });

      this.isInitialized = true;
      tokenLog('IOSLifecycleManager initialized');
      
    } catch (error) {
      tokenError('[IOSLifecycleManager] Error initializing', error);
    }
  }

  async handleAppStateChange(isActive) {
    const now = Date.now();
    

    if (isActive) {
      // App became active
      this.lastActiveTime = now;
      await this.handleAppBecameActive();
    } else {
      // App became inactive
      await this.handleAppBecameInactive();
    }
  }

  async handleAppBecameActive() {
    try {
      
      
      // Check if tokens need refresh
      const token = tokenManager.getToken();
      if (token && tokenManager.isExpiringSoon()) {
        
        
        // Import UserService dynamically to avoid circular dependency
        const { UserService } = await import('./UserService');
        const userService = new UserService();
        
        try {
          await userService.refreshToken();
          tokenLog('IOSLifecycleManager: refreshed token on activate');
        } catch (error) {
          tokenError('[IOSLifecycleManager] Token refresh failed', error);
        }
      }
      
      // Verify token storage integrity
      await this.verifyTokenStorage();
      
    } catch (error) {
      tokenError('[IOSLifecycleManager] Error handling app became active', error);
    }
  }

  async handleAppBecameInactive() {
    try {
      
      
      // Ensure tokens are persisted
      await this.persistCriticalData();
      
    } catch (error) {
      tokenError('[IOSLifecycleManager] Error handling app became inactive', error);
    }
  }

  async handleAppRestore() {
    try {
      
      
      // Check if we need to restore tokens from secure storage
      const token = tokenManager.getToken();
      if (!token) {
        
        const recovered = await this.attemptTokenRecovery();
        tokenLog('IOSLifecycleManager: app restore recovery attempted', { recovered });
      }
      
    } catch (error) {
      tokenError('[IOSLifecycleManager] Error handling app restore', error);
    }
  }

  async persistCriticalData() {
    try {
      const token = tokenManager.getToken();
      if (token) {
        // Store in secure storage as backup
        const refreshToken = await tokenManager.getStoredRefreshToken();
        if (refreshToken) {
          await secureTokenStorage.storeSecureTokens(
            token,
            refreshToken,
            tokenManager.expiration
          );
          
        }
      }
    } catch (error) {
      tokenError('[IOSLifecycleManager] Error persisting critical data', error);
    }
  }

  async verifyTokenStorage() {
    try {
      const token = tokenManager.getToken();
      if (token) {
        // Verify token is still in Preferences
        const { value: storedToken } = await Preferences.get({ key: 'access_token' });
        const { value: storedExp } = await Preferences.get({ key: 'access_token_exp' });
        
        if (!storedToken || storedToken !== token) {
          tokenWarn('[IOSLifecycleManager] Token mismatch detected, restoring from memory');
          await tokenManager.setToken(token, Math.floor((tokenManager.expiration - Date.now()) / 1000));
        }
      }
    } catch (error) {
      tokenError('[IOSLifecycleManager] Error verifying token storage', error);
    }
  }

  async attemptTokenRecovery() {
    try {
      // Import TokenRecoveryService dynamically
      const { tokenRecoveryService } = await import('./TokenRecoveryService');
      const recovered = await tokenRecoveryService.attemptTokenRecovery();
      
      if (recovered) {
        
      } else {
        
      }
      
      return recovered;
    } catch (error) {
      tokenError('[IOSLifecycleManager] Error during token recovery', error);
      return false;
    }
  }

  async cleanup() {
    try {
      if (this.backgroundTaskId) {
        // End background task if active
        this.backgroundTaskId = null;
      }
      
      
    } catch (error) {
      const log = createLogger('IOSLifecycleManager.cleanup');
      log.error('[IOSLifecycleManager] Error during cleanup:', error);
    }
  }
}

export const iosLifecycleManager = new IOSLifecycleManager(); 